import React, { useState, useEffect, useMemo } from 'react';
import isEqual from 'lodash.isequal';
import classNames from 'classnames/bind';
import { Tooltip } from '@saucelabs/ds-tooltip';
import { Button } from '@saucelabs/ds-button';
import {
  IconManualFill,
  IconFullscreenFill,
  IconPinFill,
  IconUnpinFill,
} from '@saucelabs/ds-icons';
import Placeholder from '@saucelabs/sl-images/lib/placeholder.png';

import constants from '../constants';
import { DeviceDescriptor } from '../records';
import { useStartTestButtonState } from '../hooks/useStartTestButtonState';
import Styles from './DeviceSummary.styles';
import PlatformIcon from '../../../components/PlatformIcon';

const context = classNames.bind(Styles);

type DeviceSummaryProps = {
  email?: string;
  hasSuitableAppConfigurationSelected?: boolean;
  isInUse: boolean;
  isLocked: boolean;
  isManualMinutesExpired?: boolean;
  onLaunchClick: () => void;
  onPinnedClick: () => void,
  pinned?: boolean;
  realDeviceConfiguration: DeviceDescriptor;
  unverified?: boolean;
}

const DeviceSummary: React.FC<DeviceSummaryProps> = ({
  email,
  hasSuitableAppConfigurationSelected = true,
  isInUse,
  isLocked,
  isManualMinutesExpired,
  onLaunchClick,
  onPinnedClick,
  pinned = false,
  realDeviceConfiguration, // should be directly from store
  unverified = false,
}) => {
  const [
    mostRecentlySelectedDevice,
    setMostRecentlySelectedRealDevice,
  ] = useState(realDeviceConfiguration);
  const [imageUrlBroken, setImageUrlBroken] = useState(false);

  const selectedDeviceExists = realDeviceConfiguration instanceof DeviceDescriptor;

  // realDeviceConfiguration won't exist anymore when we want to close this component,
  // but we have to persist the device data as it's closing for good UI
  useEffect(() => {
    if (selectedDeviceExists && !isEqual(realDeviceConfiguration, mostRecentlySelectedDevice)) {
      setMostRecentlySelectedRealDevice(realDeviceConfiguration);
    }
    setImageUrlBroken(false);
  }, [realDeviceConfiguration]);

  const thumbnailSrc = mostRecentlySelectedDevice?.getImageUrl() || Placeholder;
  const deviceName = mostRecentlySelectedDevice?.name || '';
  const deviceOs: string = mostRecentlySelectedDevice?.getPrettyOs() || 'unknown';
  const osVersion: string = mostRecentlySelectedDevice?.osVersion || '';
  const screenSize = mostRecentlySelectedDevice?.getPrettyResolution() || '';

  const { enableAdvise, isButtonDisabled, isDevicePermitted } = useStartTestButtonState({
    email,
    hasSuitableAppConfigurationSelected,
    isInUse,
    isLocked,
    isManualMinutesExpired,
    unverified,
  });

  return (
    <div
      className={context('device-summary-wrapper', 'fill-current', { active: selectedDeviceExists })}
      data-qa="device-summary-wrapper"
    >
      <div className={`${Styles.default} bg-gray-900`}>
        <div
          className={context('content-grid-main', 'text-white')}
          data-qa="device-summary-content"
        >
          <div className={Styles.image}>
            <div className={`${Styles['image-background-frame']} bg-white`}>
              <img
                alt={deviceName}
                className={Styles['image-device']}
                onError={() => setImageUrlBroken(true)}
                src={imageUrlBroken ? Placeholder : thumbnailSrc}
              />
            </div>
          </div>
          <div
            className={context('title', 'device-type')}
            data-qa="device-summary-real-device-name"
          >
            {deviceName}
          </div>
          <div className={context('title-os', 'title')}>OS Version</div>
          <div className={context('title-resolution', 'title')}>Resolution</div>
          <div className={Styles.os}>
            <PlatformIcon
              className={context('icon-os')}
              hasColor={false}
              name={deviceOs}
            />
            {`${deviceOs} ${osVersion}`}
          </div>
          <div className={context('resolution')}>
            <IconFullscreenFill className={Styles['icon-resolution']} />
            {screenSize}
          </div>

          <Tooltip
            placement="top"
            title={(pinned ?
              constants.TOOLTIP_LABELS.PINNED_BUTTON_ACTIVE :
              constants.TOOLTIP_LABELS.PINNED_BUTTON_INACTIVE
            )}
          >
            <button
              aria-label={`${pinned ? 'Unpin' : 'Pin'} Device`}
              className={context('button-pinned', 'bg-transparent', 'text-white',
                'border-0', 'border-l', 'border-r', 'border-solid', 'border-gray-600',
                'focus:outline-none',
                { pinned, 'text-gray-400 cursor-not-allowed': isLocked },
              )}
              data-qa="device-summary-button-pinned"
              disabled={isLocked}
              onClick={onPinnedClick}
              type="button"
            >
              {pinned ?
                <IconUnpinFill className="align-top fill-current h-2 text-blue-700 w-2" /> :
                <IconPinFill className="align-top fill-current h-2 text-gray-400 w-2" />}
            </button>
          </Tooltip>

          <Tooltip
            isOpen={!isDevicePermitted ? undefined : false}
            placement="top"
            title={enableAdvise}
          >
            <div className={Styles['start-test']}>
              <Button
                data-qa="device-summary-button-start-test"
                disabled={isButtonDisabled}
                onPress={onLaunchClick}
                prefixIcon={<IconManualFill />}
                variant="contained"
              >
                Start Test
              </Button>
            </div>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default DeviceSummary;
